/**
 * Static bucket_metric_definitions for trade-policy / execution-rules glossary UI.
 * Aligns with report input snapshot shape under market_condition_brackets.
 */
export const TRADE_POLICY_BUCKET_METRIC_DEFINITIONS: Record<string, unknown> = {
  sr: {
    metric:
      "Where entry sits between the nearest inferred support (below) and resistance (above), using distance ÷ ATR. If both support and resistance are within 0.5 ATR, the label follows the resistance check last (at_resistance wins that tie).",
    buckets: {
      unknown: {
        rule: "Support/resistance or ATR unavailable",
        meaning: "Could not place entry relative to S/R (missing levels or ATR).",
      },
      mid_range: {
        rule: "Neither distance ≤ 0.5 × ATR",
        meaning: "Not hugging an inferred support or resistance; sitting in the middle ground.",
      },
      at_support: {
        rule: "distance_to_support_atr ≤ 0.5",
        meaning: "Entry is very close to the nearest support—within half an ATR.",
      },
      at_resistance: {
        rule: "distance_to_resistance_atr ≤ 0.5",
        meaning: "Entry is very close to the nearest resistance—within half an ATR.",
      },
    },
  },
  adx: {
    metric:
      "ADX (Average Directional Index), typically 0–100: how strong a trend is, regardless of direction. Higher = stronger directional movement; lower = choppier or range-bound conditions.",
    buckets: {
      weak: {
        rule: "ADX < 18",
        meaning: "Little trend strength; price is often drifting or chopping.",
      },
      strong: {
        rule: "25 ≤ ADX < 35",
        meaning: "Clear trend environment.",
      },
      developing: {
        rule: "18 ≤ ADX < 25",
        meaning: "Trend strength is building but not yet strong.",
      },
      very_strong: {
        rule: "ADX ≥ 35",
        meaning: "Very strong trend; momentum regimes are more likely.",
      },
    },
  },
  htf: {
    metric:
      "Simple higher-timeframe alignment: fraction of evaluated 5m/30m/1h/4h trends that match your trade direction. Each timeframe counts equally (unlike htf_weighted, which weights by trend confidence).",
    buckets: {
      aligned: {
        rule: "0.5 ≤ alignment_score < 0.75",
        meaning: "Majority agreement, but not unanimous.",
      },
      unknown: {
        rule: "No usable higher-timeframe trend data at entry",
        meaning: "Not enough history or data to classify HTF trends.",
      },
      not_aligned: {
        rule: "alignment_score = 0 (with at least one valid TF evaluated)",
        meaning: "No higher timeframe trend matches your direction.",
      },
      strongly_aligned: {
        rule: "alignment_score ≥ 0.75",
        meaning: "Most or all higher timeframes point your way.",
      },
      partially_aligned: {
        rule: "0 < alignment_score < 0.5",
        meaning: "Some timeframes agree; others do not.",
      },
    },
  },
  ma50: {
    metric:
      "Price relative to the 50-period simple moving average at entry (from market overview or computed from candles).",
    buckets: {
      at: {
        rule: "Price ≈ SMA(50)",
        meaning: "Price is effectively on the average (small gap).",
      },
      above: {
        rule: "Price > SMA(50)",
        meaning: "Trading from the upper side of this medium-term average.",
      },
      below: {
        rule: "Price < SMA(50)",
        meaning: "Trading from the lower side of this medium-term average.",
      },
      unknown: {
        rule: "SMA(50) not available",
        meaning: "Insufficient data or missing snapshot to compare price to SMA50.",
      },
    },
  },
  ma200: {
    metric:
      "Price relative to the 200-period simple moving average at entry (from market overview or computed from candles).",
    buckets: {
      at: {
        rule: "Price ≈ SMA(200)",
        meaning: "Price is effectively on the average (small gap).",
      },
      above: {
        rule: "Price > SMA(200)",
        meaning: "Above this longer-term average—often interpreted as broader bullish structure.",
      },
      below: {
        rule: "Price < SMA(200)",
        meaning: "Below this longer-term average—often interpreted as broader bearish structure.",
      },
      unknown: {
        rule: "SMA(200) not available",
        meaning: "Insufficient data or missing snapshot to compare price to SMA200.",
      },
    },
  },
  bb_zone: {
    metric:
      "Where price sits inside the Bollinger bands at entry: a 0–1 score (position_ratio). 0 is at the lower band, 1 at the upper band, ~0.5 near the middle (SMA).",
    buckets: {
      mid: {
        rule: "0.4 ≤ position_ratio ≤ 0.6",
        meaning: "Near the middle band; neutral vs the envelope.",
      },
      lower: {
        rule: "0.2 ≤ position_ratio < 0.4",
        meaning: "Lower third of the band width.",
      },
      upper: {
        rule: "0.6 < position_ratio ≤ 0.8",
        meaning: "Upper third of the band width.",
      },
      lower_extreme: {
        rule: "position_ratio < 0.2",
        meaning:
          "Price is pressed against the lower band (stretched down / oversold side of the envelope).",
      },
      upper_extreme: {
        rule: "position_ratio > 0.8",
        meaning:
          "Price is pressed against the upper band (stretched up / overbought side of the envelope).",
      },
    },
  },
  squeeze: {
    metric:
      "Bollinger band width as a percent of price: (upper − lower) ÷ middle(SMA) × 100. Narrow width = bands pinched together (often called a volatility squeeze); wide = stretched bands.",
    buckets: {
      normal: {
        rule: "0.3 ≤ width_pct < 0.8 (percent of price)",
        meaning: "Typical band width for this metric’s scale.",
      },
      expanded: {
        rule: "width_pct ≥ 0.8 (percent of price)",
        meaning: "Bands are wide; price is moving a lot relative to the recent average (higher volatility).",
      },
      tight_squeeze: {
        rule: "width_pct < 0.3 (percent of price, not a 0–1 score)",
        meaning: "Bands are very tight vs price; market is unusually quiet—often precedes expansion.",
      },
    },
  },
  sr_room: {
    metric:
      "Room to the next key level in ATR multiples. For buys, distance to nearest resistance above entry; for sells, distance to nearest support below entry. ATR is used as a volatility ruler so distances are comparable across instruments.",
    buckets: {
      good: {
        rule: "0.8 × ATR ≤ room < 1.8 × ATR",
        meaning: "Moderate room—often enough context for planned targets without an immediate wall.",
      },
      wide: {
        rule: "room ≥ 1.8 × ATR",
        meaning: "Plenty of space before the next inferred support/resistance.",
      },
      tight: {
        rule: "0.3 × ATR ≤ room < 0.8 × ATR",
        meaning: "Limited room; tight maneuvering space to target.",
      },
      very_tight: {
        rule: "room < 0.3 × ATR",
        meaning: "The next level is very close—little space before price meets that barrier.",
      },
    },
  },
  atr_regime: {
    metric:
      "ATR as a percentage of price at entry: (ATR ÷ price) × 100. Measures how large typical bar moves are relative to the instrument’s price level.",
    buckets: {
      low: {
        rule: "0.15 ≤ ATR% < 0.35",
        meaning: "Below-average volatility.",
      },
      high: {
        rule: "ATR% ≥ 0.8",
        meaning: "Elevated volatility; wider swings per bar vs price.",
      },
      medium: {
        rule: "0.35 ≤ ATR% < 0.8",
        meaning: "Typical volatility for many FX/metal sessions.",
      },
      very_low: {
        rule: "ATR% < 0.15",
        meaning: "Very quiet volatility vs price.",
      },
    },
  },
  consistency: {
    metric:
      "Context consistency score (0–1): one blended measure at entry. It combines weighted HTF alignment (~55%), ADX bucket, long-term regime (trending vs transition vs ranging), and short-term direction vs your long/short. Penalties apply when short-term move fights your trade or HTF alignment is very weak. This is not the same as HTF alignment alone.",
    buckets: {
      low: {
        rule: "score < 0.45",
        meaning: "Several context pieces disagree with the trade or data is weakly aligned overall.",
      },
      high: {
        rule: "score ≥ 0.7",
        meaning: "Regime, trend strength, short-term bias, and HTFs mostly line up with the trade idea.",
      },
      medium: {
        rule: "0.45 ≤ score < 0.7",
        meaning: "Reasonable but not exceptional agreement across regime, ADX, short-term, and HTFs.",
      },
    },
  },
  sr_strength: {
    metric:
      "Strength (0–1) of the nearest relevant support/resistance zone inferred from recent highs/lows. For longs we score the nearest resistance above entry; for shorts, the nearest support below. Higher values come from more touches, fresher tests, and tighter zones (heuristic, not exchange levels).",
    buckets: {
      weak: {
        rule: "zone strength < 0.45",
        meaning: "The level looks tentative—few touches or weak recency/width profile.",
      },
      medium: {
        rule: "0.45 ≤ zone strength < 0.75",
        meaning: "Meaningful but not dominant level.",
      },
      strong: {
        rule: "zone strength ≥ 0.75",
        meaning: "Level looks well supported by recent price action (touches + recency).",
      },
    },
  },
  htf_weighted: {
    metric:
      "Higher-timeframe alignment score (0–1) weighted by trend clarity. We resample to 5m, 30m, 1h, 4h and check whether each timeframe’s trend matches your trade direction (buy → uptrend, sell → downtrend). Timeframes with stronger/clearer trend signals contribute more than fuzzy ones.",
    buckets: {
      low: {
        rule: "weighted score < 0.45",
        meaning: "Higher timeframes mostly disagree or only weakly agree with your direction.",
      },
      mid: {
        rule: "0.45 ≤ weighted score < 0.7",
        meaning: "Mixed or moderate agreement across timeframes.",
      },
      high: {
        rule: "weighted score ≥ 0.7",
        meaning: "Strong agreement on higher timeframes with your trade direction.",
      },
    },
  },
};
